import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, LayoutGrid, List, Briefcase, BarChart3, Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import LostReasonModal from "../components/LostReasonModal";
import WonConfirmModal from "../components/WonConfirmModal";
import WonExplosion from "../components/WonExplosion";
import DuplicateWarningModal from "../components/DuplicateWarningModal";
import { checkDuplicates } from "../lib/dedupe";
import FilterPopover from "../components/FilterPopover";
import PragmaSelect from "../components/PragmaSelect";
import { useSettings, formatMoney } from "../lib/settings";
import { useAuth } from "../context/AuthContext";

function daysIn(date) {
  if (!date) return 0;
  try {
    const d = new Date(date);
    return Math.floor((Date.now() - d.getTime()) / 86400000);
  } catch { return 0; }
}

// Map stage to an accent colour token used by the card stripe + count badge.
function stageAccent(stage) {
  const name = (stage?.name || "").toLowerCase();
  if (name.includes("lost")) return { dot: "#D93025", probBg: "rgba(217,48,37,0.12)", probFg: "#D93025" };
  if (name.includes("won")) return { dot: "#8FBF00", probBg: "rgba(143,191,0,0.18)", probFg: "#5A7A00" };
  if (name.includes("negotiation") || name.includes("contract")) return { dot: "#5B4FE8", probBg: "rgba(91,79,232,0.12)", probFg: "#5B4FE8" };
  if (name.includes("proposal") || name.includes("sent")) return { dot: "#8FBF00", probBg: "rgba(143,191,0,0.18)", probFg: "#5A7A00" };
  if (name.includes("qualified") || name.includes("discovery")) return { dot: "#E85B1A", probBg: "rgba(232,91,26,0.12)", probFg: "#E85B1A" };
  // Default order-based palette
  return { dot: "#5B4FE8", probBg: "rgba(91,79,232,0.12)", probFg: "#5B4FE8" };
}

// Stable per-contact gradient based on the first two characters.
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#f093fb,#f5576c)",
  "linear-gradient(135deg,#4facfe,#00f2fe)",
  "linear-gradient(135deg,#43e97b,#38f9d7)",
  "linear-gradient(135deg,#fa709a,#fee140)",
  "linear-gradient(135deg,#667eea,#764ba2)",
  "linear-gradient(135deg,#30cfd0,#330867)",
  "linear-gradient(135deg,#a18cd1,#fbc2eb)",
  "linear-gradient(135deg,#f6d365,#fda085)",
];
function avatarGradient(seed) {
  const s = (seed || "?").toString();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
}
function initialsFrom(name) {
  if (!name) return "—";
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join("").toUpperCase();
}

export default function Deals({ moduleKind = "acquisition" }) {
  const isGrowth = moduleKind === "growth";
  const moduleLabel = isGrowth ? "Growth" : "Acquisition";
  const newDealLabel = isGrowth ? "New Deal" : "New Deal";
  const navigate = useNavigate();
  const { currency } = useSettings();
  const { user } = useAuth();
  const [view, setView] = useState("kanban");
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState("");
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [pendingLostMove, setPendingLostMove] = useState(null);
  const [pendingWonMove, setPendingWonMove] = useState(null);
  const [showExplosion, setShowExplosion] = useState(false);
  const [filterState, setFilterState] = useState({ owner: [], stage: [] });
  const [growthSummary, setGrowthSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [showInsights, setShowInsights] = useState(() => {
    try { return localStorage.getItem("pragma_deals_show_insights") !== "false"; }
    catch { return true; }
  });
  const toggleInsights = () => {
    setShowInsights((v) => {
      const nv = !v;
      try { localStorage.setItem("pragma_deals_show_insights", nv ? "true" : "false"); } catch {}
      return nv;
    });
  };

  const load = async () => {
    try {
      const [p, d, c, u, co, cl] = await Promise.all([
        api.get(`/pipelines?kind=${moduleKind}`),
        api.get(`/deals?kind=${moduleKind}`),
        api.get("/contacts"), api.get("/users"),
        api.get("/companies").catch(() => ({ data: [] })),
        api.get("/clients").catch(() => ({ data: [] })),
      ]);
      setPipelines(p.data);
      setDeals(d.data); setContacts(c.data); setUsers(u.data);
      setCompanies(co.data || []); setClients(cl.data || []);
      if (!pipelineId && p.data[0]) setPipelineId(p.data[0].id);
      if (isGrowth) {
        try {
          const s = await api.get("/growth/summary");
          setGrowthSummary(s.data);
        } catch { /* summary is best-effort */ }
      }
    } catch { toast.error("Failed to load"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [moduleKind]);

  const pipeline = useMemo(() => pipelines.find(p => p.id === pipelineId), [pipelines, pipelineId]);
  const pipelineDeals = useMemo(() => {
    let rows = deals.filter(d => d.pipeline_id === pipelineId);
    if (filterState.owner.length > 0) {
      const set = new Set(filterState.owner);
      rows = rows.filter((d) => set.has(d.owner_id));
    }
    if (filterState.stage.length > 0) {
      const set = new Set(filterState.stage);
      rows = rows.filter((d) => set.has(d.stage_id));
    }
    return rows;
  }, [deals, pipelineId, filterState]);
  const stages = useMemo(() => pipeline ? [...pipeline.stages].sort((a, b) => a.order - b.order) : [], [pipeline]);

  const dealsByStage = useMemo(() => {
    const map = {};
    stages.forEach(s => { map[s.id] = []; });
    pipelineDeals.forEach(d => { if (map[d.stage_id]) map[d.stage_id].push(d); });
    return map;
  }, [pipelineDeals, stages]);

  const contactById = (cid) => contacts.find(c => c.id === cid);
  const contactName = (cid) => contactById(cid)?.name || "—";
  const clientById = (id) => clients.find(cl => cl.id === id);
  const primaryLabel = (deal) => {
    if (!deal) return "";
    if (isGrowth) {
      const cl = clientById(deal.client_id);
      if (cl?.company_name) return cl.company_name;
    }
    const co = companies.find(c => c.id === deal.company_id);
    if (co?.name) return co.name;
    const ct = contactById(deal.contact_id);
    if (ct?.company) return ct.company;
    return "";
  };
  // Legacy alias for a couple of usages below.
  const companyName = primaryLabel;

  // ─── Stat calculations ───
  const stats = useMemo(() => {
    const lostIds = new Set(stages.filter(s => (s.name || "").toLowerCase().includes("lost")).map(s => s.id));
    const wonIds  = new Set(stages.filter(s => (s.name || "").toLowerCase().includes("won")).map(s => s.id));
    const open = pipelineDeals.filter(d => !lostIds.has(d.stage_id) && !wonIds.has(d.stage_id));
    const won  = pipelineDeals.filter(d => wonIds.has(d.stage_id));

    const totalPipeline = open.reduce((a, d) => a + (Number(d.value) || 0), 0);
    const weighted = open.reduce((a, d) => a + (Number(d.value) || 0) * (Number(d.probability) || 0) / 100, 0);
    const avgAge = open.length
      ? open.reduce((a, d) => a + daysIn(d.current_stage_entered_at), 0) / open.length
      : 0;

    const now = new Date();
    const monthKey = `${now.getFullYear()}-${now.getMonth()}`;
    const wonThisMonth = won.filter(d => {
      const when = d.closed_at || d.updated_at || d.current_stage_entered_at;
      if (!when) return false;
      const dt = new Date(when);
      return `${dt.getFullYear()}-${dt.getMonth()}` === monthKey;
    });
    const wonThisMonthSum = wonThisMonth.reduce((a, d) => a + (Number(d.value) || 0), 0);

    return {
      totalPipeline, openCount: open.length,
      weighted, avgAge,
      wonThisMonthSum, wonThisMonthCount: wonThisMonth.length,
    };
  }, [pipelineDeals, stages]);

  // ─── Pipeline health segments ───
  const healthSegments = useMemo(() => {
    const lostIds = new Set(stages.filter(s => (s.name || "").toLowerCase().includes("lost")).map(s => s.id));
    const wonIds  = new Set(stages.filter(s => (s.name || "").toLowerCase().includes("won")).map(s => s.id));
    const open = pipelineDeals.filter(d => !lostIds.has(d.stage_id) && !wonIds.has(d.stage_id));
    const total = open.length || 1;
    return stages
      .filter(s => !lostIds.has(s.id) && !wonIds.has(s.id))
      .map(s => ({
        id: s.id,
        name: s.name,
        count: (dealsByStage[s.id] || []).length,
        pct: ((dealsByStage[s.id] || []).length / total) * 100,
        color: stageAccent(s).dot,
      }))
      .filter(seg => seg.count > 0);
  }, [stages, pipelineDeals, dealsByStage]);

  const firstName = user?.first_name || (user?.name || "").split(" ")[0] || "there";

  // ─── Drag-and-drop handlers (preserved) ───
  const handleDrop = async (stageId) => {
    if (!draggingId) return;
    const deal = deals.find(d => d.id === draggingId);
    if (!deal || deal.stage_id === stageId) {
      setDraggingId(null); setDragOverStage(null); return;
    }
    const targetStage = stages.find(s => s.id === stageId);
    const targetName = (targetStage?.name || "").toLowerCase();
    const isLostStage = targetName.includes("lost");
    const isWonStage = targetName.includes("won");
    const wasAlreadyLost = deal.status === "lost";
    const wasAlreadyWon = deal.status === "won";

    if (isLostStage && !wasAlreadyLost) {
      setPendingLostMove({
        dealId: draggingId,
        dealName: deal.name,
        prevStageId: deal.stage_id,
        newStageId: stageId,
      });
      setDraggingId(null); setDragOverStage(null);
      return;
    }
    if (isWonStage && !wasAlreadyWon) {
      setPendingWonMove({
        dealId: draggingId,
        dealName: deal.name,
        prevStageId: deal.stage_id,
        newStageId: stageId,
        defaultEngagementType: deal.engagement_type,
        defaultEngagementEndDate: deal.engagement_end_date,
      });
      setDraggingId(null); setDragOverStage(null);
      return;
    }
    setDeals(prev => prev.map(d => d.id === draggingId ? { ...d, stage_id: stageId } : d));
    setDraggingId(null); setDragOverStage(null);
    try {
      const { data } = await api.patch(`/deals/${draggingId}`, { stage_id: stageId });
      setDeals(prev => prev.map(d => d.id === draggingId ? data : d));
      toast.success("Deal moved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Move failed");
      load();
    }
  };

  const confirmLostDrop = async ({ reasonId, notes }) => {
    if (!pendingLostMove) return;
    const { dealId, newStageId } = pendingLostMove;
    try {
      const { data } = await api.patch(`/deals/${dealId}`, {
        stage_id: newStageId,
        lost_reason_id: reasonId,
        lost_reason_notes: notes || "",
      });
      setDeals(prev => prev.map(d => d.id === dealId ? data : d));
      toast.success("Deal marked Lost");
      setPendingLostMove(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Move failed");
    }
  };
  const cancelLostDrop = () => setPendingLostMove(null);

  const confirmWonDrop = async ({ engagementType, engagementEndDate }) => {
    if (!pendingWonMove) return;
    const { dealId, newStageId } = pendingWonMove;
    try {
      const { data } = await api.patch(`/deals/${dealId}`, {
        stage_id: newStageId,
        engagement_type: engagementType,
        engagement_end_date: engagementEndDate,
      });
      setDeals(prev => prev.map(d => d.id === dealId ? data : d));
      setPendingWonMove(null);
      setShowExplosion(true);
      toast.success(`🎉 Won! ${data.name}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Move failed");
    }
  };
  const cancelWonDrop = () => setPendingWonMove(null);

  const userName = (uid) => users.find(u => u.id === uid)?.name || "—";

  const openCount = stats.openCount;

  return (
    <div data-testid="deals-page">
      {/* ─── Stratus page header ─── */}
      <div className="stratus-page-header">
        <div>
          <div className="stratus-page-title" data-testid="page-title">{moduleLabel}</div>
          <div className="stratus-page-sub">
            {openCount} active deal{openCount === 1 ? "" : "s"}
            &nbsp;·&nbsp; Hi {firstName} <span role="img" aria-label="wave">👋</span>
          </div>
        </div>
        <div className="stratus-actions">
          <PragmaSelect
            value={pipelineId}
            onChange={setPipelineId}
            options={pipelines.map((p) => ({ value: p.id, label: p.name, hint: p.pipeline_kind === "growth" ? (p.growth_kind || "growth") : undefined }))}
            testId="pipeline-select"
            size="sm"
            style={{ minWidth: 200 }}
          />
          <FilterPopover
            testid="deals-filter"
            matchCount={pipelineDeals.length}
            value={filterState}
            onChange={setFilterState}
            groups={[
              {
                key: "owner",
                title: "Owner",
                options: users.filter((u) => u.active !== false).map((u) => ({ value: u.id, label: u.name })),
              },
              {
                key: "stage",
                title: "Stage",
                options: stages.map((s) => ({ value: s.id, label: s.name })),
              },
            ]}
          />
          <div className="view-toggle-glass">
            <button
              className={`vt-btn ${view === "kanban" ? "active" : ""}`}
              onClick={() => setView("kanban")}
              data-testid="view-kanban"
              type="button"
            >
              <LayoutGrid size={14} /> Kanban
            </button>
            <button
              className={`vt-btn ${view === "list" ? "active" : ""}`}
              onClick={() => setView("list")}
              data-testid="view-list"
              type="button"
            >
              <List size={14} /> List
            </button>
          </div>
          <button
            className="pg-btn"
            onClick={toggleInsights}
            data-testid="toggle-insights"
            type="button"
            title={showInsights ? "Hide insights and show only the pipeline" : "Show insights"}
          >
            {showInsights ? <><EyeOff size={14} /> Hide insights</> : <><BarChart3 size={14} /> Show insights</>}
          </button>
          <button
            className="btn-pill-primary"
            onClick={() => setShowForm(true)}
            data-testid="new-deal-btn"
            type="button"
          >
            <Plus size={14} strokeWidth={2.5} /> New Deal
          </button>
        </div>
      </div>

      {/* ─── Growth summary row (Growth module only) ─── */}
      {showInsights && isGrowth && growthSummary && (
        <div className="stats-row" data-testid="growth-summary-row">
          <div className="stat-card" data-testid="growth-stat-renewals-90d">
            <div className="stat-label">Renewals · Next 90d</div>
            <div className="stat-value">{formatMoney(growthSummary.renewals_next_90d_value || 0, currency.symbol)}</div>
            <div className="stat-badge badge-blue">{growthSummary.renewals_next_90d_count || 0} deal{(growthSummary.renewals_next_90d_count || 0) === 1 ? "" : "s"}</div>
          </div>
          <div className="stat-card" data-testid="growth-stat-upsell-pipeline">
            <div className="stat-label">Upsell Pipeline</div>
            <div className="stat-value">{formatMoney(growthSummary.upsell_pipeline_value || 0, currency.symbol)}</div>
            <div className="stat-sub">All open upsell deals</div>
          </div>
          <div className="stat-card" data-testid="growth-stat-weighted">
            <div className="stat-label">Weighted Growth</div>
            <div className="stat-value">{formatMoney(growthSummary.weighted_growth_pipeline || 0, currency.symbol)}</div>
            <div className="stat-sub">Probability adjusted</div>
          </div>
          <div className="stat-card" data-testid="growth-stat-expiring-30d">
            <div className="stat-label">Expiring · Next 30d</div>
            <div className="stat-value">{growthSummary.deliverables_expiring_30d || 0}</div>
            <div className={`stat-badge ${(growthSummary.deliverables_expiring_30d || 0) > 0 ? "badge-amber" : "badge-green"}`}>deliverable{(growthSummary.deliverables_expiring_30d || 0) === 1 ? "" : "s"}</div>
          </div>
        </div>
      )}

      {/* ─── Stat cards ─── */}
      {showInsights && (
      <div className="stats-row" data-testid="deals-stats-row">
        <div className="stat-card" data-testid="stat-total-pipeline">
          <div className="stat-label">Total Pipeline</div>
          <div className="stat-value">{formatMoney(stats.totalPipeline, currency.symbol)}</div>
          <div className="stat-badge badge-blue">{stats.openCount} deal{stats.openCount === 1 ? "" : "s"}</div>
        </div>
        <div className="stat-card" data-testid="stat-weighted-value">
          <div className="stat-label">Weighted Value</div>
          <div className="stat-value">{formatMoney(stats.weighted, currency.symbol)}</div>
          <div className="stat-sub">Probability adjusted</div>
        </div>
        <div className="stat-card" data-testid="stat-avg-age">
          <div className="stat-label">Avg. Deal Age</div>
          <div className="stat-value">{stats.avgAge.toFixed(1)}d</div>
          <div className={`stat-badge ${stats.avgAge < 14 ? "badge-green" : stats.avgAge < 30 ? "badge-amber" : "badge-red"}`}>
            {stats.avgAge < 14 ? "On track" : stats.avgAge < 30 ? "Watch" : "Stale"}
          </div>
        </div>
        <div className="stat-card" data-testid="stat-closed-won">
          <div className="stat-label">Closed Won</div>
          <div className="stat-value">{formatMoney(stats.wonThisMonthSum, currency.symbol)}</div>
          <div className={`stat-badge ${stats.wonThisMonthCount > 0 ? "badge-green" : "badge-amber"}`}>
            {stats.wonThisMonthCount} this month
          </div>
        </div>
      </div>
      )}

      {/* ─── Pipeline health ─── */}
      {showInsights && (
      <div className="pipeline-bar" data-testid="pipeline-health-bar">
        <span className="pipeline-label">Pipeline health</span>
        <div className="pipeline-track">
          {healthSegments.length === 0 && (
            <div className="pipeline-seg" style={{ width: "100%", background: "rgba(180,188,210,0.45)", borderRadius: 4 }} />
          )}
          {healthSegments.map(seg => (
            <div
              key={seg.id}
              className="pipeline-seg"
              style={{ width: `${seg.pct}%`, background: seg.color, borderRadius: 4 }}
              title={`${seg.name}: ${seg.count} deal${seg.count === 1 ? "" : "s"}`}
            />
          ))}
        </div>
        <span className="pipeline-hint">
          {healthSegments.length === 0
            ? "No open deals"
            : healthSegments.map(s => `${s.name} ${Math.round(s.pct)}%`).join(" · ")}
        </span>
      </div>
      )}

      {/* ─── Kanban / List ─── */}
      {view === "kanban" ? (
        <div className="kanban-wrap">
          <div className="kanban-stratus">
            {stages.map(stage => {
              const stageDeals = dealsByStage[stage.id] || [];
              const total = stageDeals.reduce((a, d) => a + (Number(d.value) || 0), 0);
              const acc = stageAccent(stage);
              return (
                <div
                  key={stage.id}
                  className={`k-col ${dragOverStage === stage.id ? "drop-target" : ""}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage.id); }}
                  onDragLeave={() => setDragOverStage(null)}
                  onDrop={() => handleDrop(stage.id)}
                  data-testid={`stage-col-${stage.id}`}
                >
                  <div className="k-col-header">
                    <div className="k-col-header-left">
                      <div className="k-col-dot" style={{ background: acc.dot }} />
                      <div>
                        <div className="k-col-name">{stage.name}</div>
                        <div className="k-col-meta">{stage.probability}% · {stageDeals.length} deal{stageDeals.length === 1 ? "" : "s"}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div className="k-col-amount">{formatMoney(total, currency.symbol)}</div>
                      <div className="k-col-count">{stageDeals.length}</div>
                    </div>
                  </div>

                  <div className="k-col-body">
                    <button
                      type="button"
                      className="k-col-add"
                      onClick={() => setShowForm(true)}
                      data-testid={`stage-add-${stage.id}`}
                      style={{ marginBottom: 10 }}
                    >
                      <Plus strokeWidth={2.5} /> Add deal
                    </button>
                    {stageDeals.map(d => {
                      const ct = contactById(d.contact_id);
                      const cn = companyName(d);
                      return (
                        <div
                          key={d.id}
                          className={`k-deal ${draggingId === d.id ? "dragging" : ""}`}
                          style={{ ["--k-accent"]: acc.dot, ["--k-prob-bg"]: acc.probBg, ["--k-prob-fg"]: acc.probFg }}
                          draggable
                          onDragStart={() => setDraggingId(d.id)}
                          onDragEnd={() => setDraggingId(null)}
                          onClick={() => navigate(`/deals/${d.id}`)}
                          data-testid={`deal-card-${d.id}`}
                        >
                          <div className="k-deal-company">{cn || d.name}</div>
                          {cn && <div className="k-deal-name">{d.name}</div>}
                          {ct && (
                            <div className="k-deal-contact">
                              <div className="k-contact-avatar" style={{ background: avatarGradient(ct.name) }}>
                                {initialsFrom(ct.name)}
                              </div>
                              <span className="k-contact-name">{ct.name}</span>
                            </div>
                          )}
                          <div className="k-deal-footer">
                            <div className={`k-deal-value ${(!d.value || d.value === 0) ? "zero" : ""}`}>
                              {formatMoney(d.value, currency.symbol)}
                            </div>
                            <div className="k-deal-tags">
                              <span className="k-deal-prob">{d.probability}%</span>
                              <span className="k-deal-age">{daysIn(d.current_stage_entered_at)}d ago</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {stageDeals.length === 0 && (
                      <div className="k-col-empty">
                        <Briefcase strokeWidth={1.5} />
                        drop deal here
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="pg-card" style={{ overflowX: "auto" }}>
          <table className="pg-table">
            <thead>
              <tr>
                <th>Name</th><th>Contact</th><th>Value</th><th>Stage</th>
                <th>Probability</th><th>Owner</th><th>Close Date</th><th>Days in Stage</th>
              </tr>
            </thead>
            <tbody>
              {pipelineDeals.map(d => {
                const s = stages.find(x => x.id === d.stage_id);
                return (
                  <tr key={d.id} onClick={() => navigate(`/deals/${d.id}`)}>
                    <td>{d.name}</td>
                    <td className="text-dim">{contactName(d.contact_id)}</td>
                    <td className="mono" style={{ color: "var(--pg-text)" }}>{formatMoney(d.value, currency.symbol)}</td>
                    <td className="text-dim">{s?.name || "—"}</td>
                    <td className="mono text-xs text-dim">{d.probability}%</td>
                    <td className="text-dim text-sm">{userName(d.owner_id)}</td>
                    <td className="mono text-xs text-dim">{(d.expected_close_date || "").slice(0, 10) || "—"}</td>
                    <td className="mono text-xs text-dim">{daysIn(d.current_stage_entered_at)}d</td>
                  </tr>
                );
              })}
              {pipelineDeals.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-mute mono text-xs">No deals in this pipeline</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <NewDealForm
          pipelines={pipelines}
          contacts={contacts}
          companies={companies}
          clients={clients}
          users={users}
          moduleKind={moduleKind}
          initialPipelineId={pipelineId}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}

      {pendingLostMove && (
        <LostReasonModal
          dealName={pendingLostMove.dealName}
          onCancel={cancelLostDrop}
          onConfirm={confirmLostDrop}
        />
      )}

      {pendingWonMove && (
        <WonConfirmModal
          dealName={pendingWonMove.dealName}
          defaultEngagementType={pendingWonMove.defaultEngagementType}
          defaultEngagementEndDate={pendingWonMove.defaultEngagementEndDate}
          onCancel={cancelWonDrop}
          onConfirm={confirmWonDrop}
        />
      )}

      {showExplosion && (
        <WonExplosion onDone={() => setShowExplosion(false)} />
      )}
    </div>
  );
}

function NewDealForm({ pipelines, contacts, companies, clients, users, moduleKind, initialPipelineId, onClose, onCreated }) {
  const isGrowth = moduleKind === "growth";
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [pid, setPid] = useState(initialPipelineId);
  const pipeline = pipelines.find(p => p.id === pid);
  const stages = pipeline ? [...pipeline.stages].sort((a, b) => a.order - b.order) : [];
  const [form, setForm] = useState({
    name: "", pipeline_id: pid,
    stage_id: stages[0]?.id || "",
    company_id: "", client_id: "", contact_id: "",
    owner_id: currentUser?.id || users[0]?.id,
    expected_close_date: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [dedupeMatches, setDedupeMatches] = useState(null);

  useEffect(() => {
    setForm(f => ({ ...f, pipeline_id: pid, stage_id: stages[0]?.id || "" }));
    // eslint-disable-next-line
  }, [pid]);

  // Selected company_id — for Growth we derive it from the picked Client.
  const selectedCompanyId = isGrowth
    ? (clients.find(cl => cl.id === form.client_id)?.company_id || "")
    : form.company_id;

  // Filter contacts to those in the selected company (falls back to all
  // contacts when no company is picked yet — same UX as before).
  const eligibleContacts = selectedCompanyId
    ? contacts.filter(c => c.company_id === selectedCompanyId)
    : contacts;

  // Reset contact if company changes and current contact is no longer in it.
  useEffect(() => {
    if (form.contact_id && selectedCompanyId) {
      const stillValid = eligibleContacts.some(c => c.id === form.contact_id);
      if (!stillValid) setForm(f => ({ ...f, contact_id: "" }));
    }
    // eslint-disable-next-line
  }, [selectedCompanyId]);

  const doCreate = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // For Growth deals, promote the Client's company_id + client_id.
      if (isGrowth && form.client_id) {
        const cl = clients.find(x => x.id === form.client_id);
        if (cl?.company_id) payload.company_id = cl.company_id;
        payload.deal_kind = "upsell"; // manual growth = upsell (renewals auto-created)
      } else {
        payload.deal_kind = "acquisition";
      }
      if (!payload.contact_id) delete payload.contact_id;
      if (!payload.company_id) delete payload.company_id;
      if (!payload.client_id) delete payload.client_id;
      if (!payload.expected_close_date) delete payload.expected_close_date;
      await api.post("/deals", payload);
      toast.success("Deal created");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setSaving(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isGrowth && !form.client_id) {
      toast.error("Pick a Client");
      return;
    }
    if (!isGrowth && !form.company_id) {
      toast.error("Pick a Company");
      return;
    }
    setSaving(true);
    const { hasMatches, matches } = await checkDuplicates({
      entity_type: "deal",
      contact_id: form.contact_id || undefined,
      company_id: selectedCompanyId || undefined,
      name: form.name,
    });
    setSaving(false);
    if (hasMatches) { setDedupeMatches(matches); return; }
    doCreate();
  };

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} />
      <div className="side-panel" data-variant="center" data-testid="new-deal-panel">
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h2 className="text-xl">{isGrowth ? "New Growth Deal" : "New Deal"}</h2>
          <div className="mono text-[11px] text-mute mt-1">
            Deal Value is auto-calculated from Deliverables — add them on the deal page after creating.
          </div>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Name</label>
            <input className="pg-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="deal-input-name" />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Pipeline</label>
            <PragmaSelect
              value={pid}
              onChange={setPid}
              options={pipelines.map((p) => ({ value: p.id, label: p.name, hint: p.pipeline_kind === "growth" ? (p.growth_kind || "growth") : undefined }))}
            />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Stage</label>
            <PragmaSelect
              value={form.stage_id}
              onChange={(v) => setForm({ ...form, stage_id: v })}
              options={stages.map((s) => ({ value: s.id, label: s.name, hint: `${s.probability}% win probability` }))}
            />
          </div>

          {isGrowth ? (
            <div>
              <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Client<span style={{ color: "var(--pg-red)" }}> *</span></label>
              <PragmaSelect
                value={form.client_id}
                onChange={(v) => setForm({ ...form, client_id: v })}
                options={[{ value: "", label: "— pick a client —" }, ...clients.map((cl) => ({ value: cl.id, label: cl.company_name || "(no name)" }))]}
                placeholder="— pick a client —"
                testId="deal-input-client"
              />
              {clients.length === 0 && (
                <div className="mono text-[10px] text-mute mt-1">
                  No clients yet — win an Acquisition deal first to create one.
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Company<span style={{ color: "var(--pg-red)" }}> *</span></label>
              <PragmaSelect
                value={form.company_id}
                onChange={(v) => setForm({ ...form, company_id: v })}
                options={[{ value: "", label: "— pick a company —" }, ...companies.map((co) => ({ value: co.id, label: co.name }))]}
                placeholder="— pick a company —"
                testId="deal-input-company"
              />
            </div>
          )}

          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">
              Contact <span className="text-mute" style={{ textTransform: "none", fontSize: 10 }}>· optional</span>
            </label>
            <PragmaSelect
              value={form.contact_id}
              onChange={(v) => setForm({ ...form, contact_id: v })}
              options={[{ value: "", label: "— none —" }, ...eligibleContacts.map((c) => ({ value: c.id, label: c.name }))]}
              placeholder="— none —"
              testId="deal-input-contact"
            />
            {selectedCompanyId && eligibleContacts.length === 0 && (
              <div className="mono text-[10px] text-mute mt-1">No active contacts in that company yet.</div>
            )}
          </div>

          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Owner</label>
            <PragmaSelect
              value={form.owner_id}
              onChange={(v) => setForm({ ...form, owner_id: v })}
              options={users.map((u) => ({ value: u.id, label: u.name, hint: u.job_title || u.email }))}
            />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Expected Close Date</label>
            <input className="pg-input" type="date" value={form.expected_close_date} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} />
          </div>
        </form>
        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button type="button" className="pg-btn" onClick={onClose}>Cancel</button>
          <button onClick={submit} className="pg-btn pg-btn-primary" disabled={saving} data-testid="deal-submit">{saving ? "Saving…" : "Create"}</button>
        </div>
      </div>
      {dedupeMatches && (
        <DuplicateWarningModal
          matches={dedupeMatches}
          entityLabel="Deal"
          onCancel={() => setDedupeMatches(null)}
          onContinue={() => { setDedupeMatches(null); doCreate(); }}
          onOpenExisting={(row) => { setDedupeMatches(null); onClose(); navigate(`/deals/${row.id}`); }}
        />
      )}
    </>
  );
}
